import { Injectable } from '@angular/core';
import { IConfigService } from './iconfig.service';
import { ApiService, ApiCacheOptions } from '../../shared/index';
import { AppContext } from './app-context.model';
import { ConfigSetting } from './config-setting.model';
import { AssociationInfo } from './association-info.model';

@Injectable()
export class ConfigService implements IConfigService {
  public _apiService: ApiService;
  public _cachedAssocInfo: AssociationInfo;
  public _cacheOptions: ApiCacheOptions;
  private _hasBetaFeatureCache: Array<Promise<boolean>> = [];
  private _noCache: ApiCacheOptions;

  public constructor(apiService: ApiService) {
    this._apiService = apiService;
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
    this._noCache = this._apiService.getDefaultApiCacheOptionsForNoCache();
  }

  public getAppContext = (): Promise<AppContext> => {
    return this._apiService.get('Config/AppContext', this._cacheOptions).then((data: AppContext) => {
      return Promise.resolve(data);
    });
  };

  public getAssociationInfo(): Promise<AssociationInfo> {
    if (this._cachedAssocInfo) return Promise.resolve(this._cachedAssocInfo);

    return this._apiService.get('Config/AssociationInfo', this._cacheOptions).then((assocInfo) => {
      if (assocInfo) this._cachedAssocInfo = assocInfo;

      return assocInfo;
    });
  }

  public getAssociationInfoUncached(): Promise<AssociationInfo> {
    return this._apiService.get('Config/AssociationInfo', this._noCache).then((assocInfo) => {
      if (assocInfo) this._cachedAssocInfo = assocInfo;

      return assocInfo;
    });
  }

  public updateAssociationInfo(value: AssociationInfo): Promise<void> {
    this._cachedAssocInfo = null;

    return this._apiService.put('Config/AssociationInfo', value);
  }

  public getWebRoot = (): Promise<string> => {
    return this._apiService.get('Config/WebRoot', this._cacheOptions).then((data: string) => {
      return Promise.resolve(data);
    });
  };

  public getConfigSetting = (settingName: string): Promise<ConfigSetting> => {
    return this._apiService.get('ConfigSettings/' + settingName, this._cacheOptions).then((data: ConfigSetting) => {
      return Promise.resolve(data);
    });
  };

  /**
   * checks if current customer has access to a beta feature
   * @param featureName
   */
  public hasBetaFeatureEnabled(featureName: string): Promise<boolean> {
    if (!featureName) return Promise.resolve(false);

    let lowerName: string = featureName.toLowerCase();

    if (this._hasBetaFeatureCache[lowerName]) return this._hasBetaFeatureCache[lowerName];

    // cache the response
    this._hasBetaFeatureCache[lowerName] = this._apiService
      .get('/Config/HasBetaFeature?name=' + encodeURIComponent(featureName), this._cacheOptions)
      .then(
        (hasIt) => {
          return hasIt;
        },
        (err) => {
          delete this._hasBetaFeatureCache[lowerName];
          return err;
        }
      );

    return this._hasBetaFeatureCache[lowerName];
  }

  public toggleBetaFeature(featureName: string): Promise<boolean> {
    return this._apiService.post('Config/EnableBetaFeature', featureName);
  }

  public getCreditCardTypes(): Promise<string[]> {
    return this._apiService.get('Config/CardTypes');
  }

  public getAssociationInfoLogoUrl(): Promise<string> {
    return this._apiService.get('Config/AssociationInfo/Logo');
  }

  public updateAssociationInfoLogoUrl(url: string) {
    return this._apiService.put('Config/AssociationInfo/Logo', url);
  }
}
